import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import viewingHistoryService from '../../../services/viewingHistoryService';

const RecentlyViewedVendors = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hardcodedFallback] = useState([
    {
      id: 1,
      name: 'Elegant Banquet Hall',
      category: 'Venue',
      location: 'San Francisco',
      rating: 4.6,
      reviewCount: 89,
      startingPrice: 8000,
      image: 'https://images.unsplash.com/photo-1519167758481-83f29d8ae8c4?w=300&h=200&fit=crop',
      viewedAt: '2 hours ago',
      verified: true
    },
    {
      id: 2,
      name: 'Desi Delights Catering',
      category: 'Catering',
      location: 'Bay Area',
      rating: 4.8,
      reviewCount: 156,
      startingPrice: 65,
      image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=300&h=200&fit=crop',
      viewedAt: '1 day ago',
      verified: true
    },
    {
      id: 3,
      name: 'Aarti Wedding Photography',
      category: 'Photography',
      location: 'San Francisco',
      rating: 4.9,
      reviewCount: 234,
      startingPrice: 2800,
      image: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=300&h=200&fit=crop',
      viewedAt: '2 days ago',
      verified: true
    },
    {
      id: 4,
      name: 'Bollywood Sound System',
      category: 'Audio/Visual',
      location: 'South Bay',
      rating: 4.4,
      reviewCount: 78,
      startingPrice: 1200,
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=300&h=200&fit=crop',
      viewedAt: '3 days ago',
      verified: false
    },
    {
      id: 5,
      name: 'Regal Decorations',
      category: 'Decor',
      location: 'San Francisco',
      rating: 4.7,
      reviewCount: 167,
      startingPrice: 3500,
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=200&fit=crop',
      viewedAt: '1 week ago',
      verified: true
    },
    {
      id: 6,
      name: 'Glamour Bridal Boutique',
      category: 'Bridal Wear',
      location: 'Bay Area',
      rating: 4.5,
      reviewCount: 92,
      startingPrice: 1200,
      image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=300&h=200&fit=crop',
      viewedAt: '1 week ago',
      verified: true
    }
  ]);

  // Load recently viewed vendors from database (with localStorage fallback)
  useEffect(() => {
    const loadRecentlyViewed = async () => {
      setIsLoading(true);
      try {
        // Try loading from database first if user is logged in
        if (user?.id) {
          const result = await viewingHistoryService.getRecentlyViewed(user.id, 6);

          if (result.success && result.data && result.data.length > 0) {
            setRecentlyViewed(result.data);
            setIsLoading(false);
            return;
          }
        }

        // Fallback to localStorage for guest users or if database fails
        const viewingHistory = localStorage.getItem(`viewingHistory_${user?.id || 'guest'}`);
        if (viewingHistory) {
          const parsedHistory = JSON.parse(viewingHistory);
          const sortedHistory = parsedHistory
            .sort((a, b) => new Date(b.viewedAt) - new Date(a.viewedAt))
            .slice(0, 6);
          setRecentlyViewed(sortedHistory);
        } else {
          // Use fallback data if no history found
          setRecentlyViewed(hardcodedFallback);
        }
      } catch (error) {
        console.error('Error loading viewing history:', error);
        setRecentlyViewed(hardcodedFallback);
      } finally {
        setIsLoading(false);
      }
    };

    loadRecentlyViewed();
  }, [user, hardcodedFallback]);

  const handleViewVendor = (vendor) => {
    // Navigate to vendor profile using URL params instead of state
    navigate(`/vendor-profile/${vendor.id}`);
  };

  const handleContactVendor = (vendor) => {
    // Navigate to vendor profile and open contact form
    navigate(`/vendor-profile/${vendor.id}`, {
      state: {
        openContact: true
      }
    });
  };

  const handleClearHistory = async () => {
    if (window.confirm('Are you sure you want to clear your viewing history?')) {
      try {
        // Clear from database if user is logged in
        if (user?.id) {
          const result = await viewingHistoryService.clearHistory(user.id);
          if (!result.success) {
            console.warn('Failed to clear database history:', result.error);
          }
        }

        // Also clear localStorage
        localStorage.removeItem(`viewingHistory_${user?.id || 'guest'}`);
        setRecentlyViewed([]);
        showToast('Viewing history cleared successfully', 'success');
      } catch (error) {
        console.error('Error clearing history:', error);
        showToast('Failed to clear viewing history', 'error');
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Recently Viewed
          </h3>
          <p className="text-text-secondary text-sm">
            Vendors you've recently browsed
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Trash2"
          onClick={handleClearHistory}
        >
          Clear History
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading viewing history...</p>
        </div>
      ) : recentlyViewed.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recentlyViewed.map((vendor) => (
            <div key={vendor.id} className="border border-border-light rounded-lg p-4 hover:shadow-medium transition-all duration-200">
              <div className="relative mb-3">
                <img
                  src={vendor.image}
                  alt={vendor.name}
                  className="w-full h-32 rounded-lg object-cover"
                />
                <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded-full text-xs">
                  {vendor.viewedAt}
                </div>
                {vendor.verified && (
                  <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                    <Icon name="CheckCircle" size={12} className="inline mr-1" />
                    Verified
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-text-primary line-clamp-1">
                  {vendor.name}
                </h4>
                <p className="text-text-secondary text-sm">{vendor.category}</p>
                <p className="text-text-muted text-sm flex items-center">
                  <Icon name="MapPin" size={14} className="mr-1" />
                  {vendor.location}
                </p>

                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={14} className="text-yellow-500" fill="currentColor" />
                    <span className="text-sm font-medium text-text-primary">{vendor.rating}</span>
                  </div>
                  <span className="text-text-muted text-sm">({vendor.reviewCount} reviews)</span>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <span className="text-primary font-semibold">
                    From {formatPrice(vendor.startingPrice)}
                  </span>
                </div>

                <div className="flex space-x-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewVendor(vendor)}
                    className="flex-1"
                  >
                    View Again
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleContactVendor(vendor)}
                    className="flex-1"
                  >
                    Contact
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="Eye" size={48} className="text-text-muted mx-auto mb-4" />
          <p className="text-text-secondary mb-4">No recently viewed vendors</p>
          <Button
            variant="primary"
            size="sm"
            iconName="Search"
            onClick={() => navigate('/all-vendors')}
          >
            Browse Vendors
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecentlyViewedVendors;